import { isString } from 'lodash';
import { parseURL } from './helpers';
/**
 * # WebUrl Utility
 *
 * WebUrl is a class based on the native methods from URL (Native URL object) which
 * helps to construct URLs and add query parameters.
 *
 * The constructor takes either a string URL or a native URL object. The class helps
 * in parsing, validating, and preparing URL objects and is compatible with all
 * JavaScript environments.
 *
 * WebUrl supports both absolute and relative URLs. If you pass a relative URL, the
 * URL will be treated as relative and operations like toString() will only output
 * the relative URL. Otherwise, the URL will be treated as an absolute URL. A relative
 * URL can be made absolute by calling `makeAbsolute`.
 *
 * ## Usage
 *
 * 1. WebUrl Object
 *
 *  ```
 *  const webUrlEx1 = new WebUrl('/');
 *  ```
 *
 * 2. `setQuery`: Returns the WebUrl object with the passed query params(object) applied to the URL, replacing existing
 * query params when the replace flag is set to true (the default)
 *
 *  ```
 *  const webUrlEx2 = new WebUrl('https://target.com/my/path?one=yes&two=no').setQuery({ two: 'mind' }, true);
 *
 *  // OR
 *
 *  const webUrlEx2 = new WebUrl('https://target.com/my/path?one=yes&two=no').setQuery({ two: 'mind' }, false);
 *  ```
 *
 * 3. `getQuery`: Returns the parsed query parameters in the form of an object.
 *
 * ```
 *  const webUrlEx3 = new WebUrl('https://target.com/my/path?one=yes&two=no&three=');
 *  const query = webUrlEx3.getQuery();
 *  // output : {
 *  //   one: 'yes',
 *  //   two: 'no',
 *  //   three: '',
 *  // }
 *  ```
 *
 * 4. `removeQuery`: Removes the given query param(s), if present, from the URL
 *
 * ```
 *  const webUrlEx4 = new WebUrl('https://target.com/my/path?one=yes&two=no');
 *  webUrlEx4.removeQuery('two');
 *  // webUrlEx4.toString() === 'https://target.com/my/path?one=yes'
 *
 * 5. `clearQuery`: Clears the entire search query from the URL
 *
 * ```
 *  const webUrlEx5 = new WebUrl('https://target.com/my/path?one=yes&two=no');
 *  webUrlEx5.clearQuery();
 *  webUrlEx5.toString() === 'https://target.com/my/path';
 * ```
 *
 * 6. `isRelative` property: Indicates whether or not the URL is a relative URL.
 *
 * ```
 *  const absoluteWebUrl = new WebUrl('https://www.target.com');
 *  absoluteWebUrl.isRelative; // false
 *
 *  const relativeWebUrl = new WebUrl('/my/path');
 *  relativeWebUrl.isRelative; // true
 *
 *  const queryStringUrl = new WebUrl('?docs=example');
 *  queryStringUrl.isRelative; // true
 *
 *  const hashUrl = new WebUrl('#hash-example');
 *  hashUrl.isRelative; // true
 * ```
 *
 * 7. `makeAbsolute`: Converts a relative WebUrl into an absolute WebUrl with
 * the given baseUrl.
 *
 * ```
 *  const webUrlEx7 = new WebUrl('/my/path');
 *  webUrlEx7.makeAbsolute('https://www.target.com').toString() === 'https://www.target.com/my/path'; // true
 * ```
 *
 * 8. `makeRelative`: Convert this WebUrl into an relative WebUrl.
 *
 * ```
 *  const webUrlEx7 = new WebUrl('https://www.target.com/my/path');
 *  webUrlEx7.makeRelative().toString() === '/my/path'; // true
 * ```
 *
 * 9. `equals` and `matches`: compares URLs
 *
 * Both `equals` and `matches` compare two different URLs to see if they serialize to the same
 * URL string. They can compare against WebUrl, URL, and string instances. The main difference is
 * that `matches` disregards query string order.
 *
 * ```
 * new WebUrl('https://www.target.com?one=1&two=2').equals('https://www.target.com?two=2&one=1'); // false, different query string order
 * new WebUrl('https://www.target.com?one=1&two=2').matches('https://www.target.com?two=2&one=1'); // true, different query string order is ignored
 *
 * ```
 */
export class WebUrl {
    constructor(url) {
        WebUrl.validateArgs(url);
        if (url instanceof URL) {
            this.url = url;
            this._isRelative = false;
        }
        else {
            this.url = parseURL(url);
            this._isRelative = url[0] === '?' || url[0] === '#' || (url[0] === '/' && url[1] !== '/');
        }
        return this;
    }
    get hash() {
        return this.url.hash;
    }
    get host() {
        return this.isRelative ? '' : this.url.host;
    }
    get hostname() {
        return this.isRelative ? '' : this.url.hostname;
    }
    get href() {
        return this.isRelative
            ? `${this.url.pathname}${this.url.search}${this.url.hash}`
            : this.url.toString();
    }
    /**
     * Get whether or not this WebUrl is a relative URL.
     *
     * Returns `true` if it is a relative URL, false otherwise.
     */
    get isRelative() {
        return this._isRelative;
    }
    get origin() {
        return this.isRelative ? '' : this.url.origin;
    }
    get password() {
        return this.isRelative ? '' : this.url.password;
    }
    get pathname() {
        return this.url.pathname;
    }
    get port() {
        return this.isRelative ? '' : this.url.port;
    }
    get protocol() {
        return this.isRelative ? '' : this.url.protocol;
    }
    get search() {
        return this.url.search;
    }
    get searchParams() {
        return this.url.searchParams;
    }
    get username() {
        return this.isRelative ? '' : this.url.username;
    }
    /**
     * To Decode the string using decodeURIComponent to prepare the final query string for URL.
     *
     * @param query The string query to decode
     * @param escapeQuerySpace Whether or not to encode spaces with `%20`
     */
    static decodeQuery(query, escapeQuerySpace = true) {
        const queryString = String(query);
        try {
            return decodeURIComponent(escapeQuerySpace ? queryString.replace(/\+/g, '%20') : queryString);
        }
        catch {
            return queryString;
        }
    }
    /**
     * To add the given params using searchParams.set to the url (Native URL Object or string).
     *
     * Note: parameters whose value is undefined are not added
     *
     * @param url The raw URL or string instance to append parameters to
     * @param params An object containing key/value pairs representing the query string
     */
    static buildURLWithParams(url, newParams) {
        var _a;
        WebUrl.validateArgs(url);
        const webUrl = new WebUrl(url);
        const params = webUrl.searchParams;
        for (const [key, value] of Object.entries(newParams)) {
            if (value === undefined) {
                params.delete(key);
            }
            else {
                params.set(key, (_a = value === null || value === void 0 ? void 0 : value.toString()) !== null && _a !== void 0 ? _a : value);
            }
        }
        return webUrl.toString();
    }
    /**
     * Validate arguments passed to the constructor
     *
     * @param url
     */
    static validateArgs(url) {
        if (!isString(url) && !(url instanceof URL))
            throw new TypeError('Invalid argument supplied to WebUrl constructor');
        return true;
    }
    /**
     * Convert this WebUrl into an absolute URL type using the given baseUrl.
     *
     * Notes:
     *
     * 1. This method is meant to be called only once on a relative WebUrl
     * instance. Calling it on an absolute WebUrl instance will have no effect
     * and may have unattended consequences in your code.
     *
     * @param baseUrl The baseUrl to use for the absolute URL (defaults to https://www.target.com)
     */
    makeAbsolute(baseUrl = 'https://www.target.com') {
        if (!this.isRelative) {
            // warn devs during development
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn("You're calling `makeAbsolute` on a non-relative url. This is a noop and could lead to bugs.");
            }
            return this;
        }
        WebUrl.validateArgs(baseUrl);
        try {
            this.url = new URL(`${this.url.pathname}${this.url.search}${this.url.hash}`, baseUrl);
            this._isRelative = false;
        }
        catch (error) {
            throw new Error(`${error.message}: ${baseUrl}`);
        }
        return this;
    }
    /**
     * Convert this WebUrl into an relative WebUrl.
     *
     * Notes:
     *
     * 1. This method is meant to be called only once on an absolute WebUrl
     * instance. Calling it on a relative WebUrl instance will have no effect
     * and may have unintended consequences in your code.
     */
    makeRelative() {
        if (this.isRelative) {
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn("You're calling `makeRelative` on a relative url. This is a noop and could lead to bugs.");
            }
            return this;
        }
        this._isRelative = true;
        return this;
    }
    /**
     * The method will parse the query from the URL and will return the object
     * thus made.
     *
     * @param escapeQuerySpace Whether or not escape spaces using `%20`
     */
    getQuery(escapeQuerySpace = true) {
        const items = {};
        this.searchParams.forEach((value, key) => {
            const name = WebUrl.decodeQuery(key, escapeQuerySpace);
            const finalValue = WebUrl.decodeQuery(value, escapeQuerySpace);
            items[name] = finalValue;
        });
        return items;
    }
    /**
     * The method is used to add params in the form of an Object. Please note: this method specially designed to add collection
     * of key-value pairs. Applicable for the cases when you have a object of params.
     *
     * e.g.
     *
     * ```
     * {
     *  "key1": value1
     *  "key2": value2
     *  "key3": value3
     * }
     * ```
     *
     * If a key's value is undefined, the key and value will not be added to the query.
     *
     * The `replace` flag helps in the determining whether to replace the value of already present key inside the oldParams (if present):
     *
     * - if `replace` is true, replace the value of key1 to new value present in newParams
     * - if `replace` is false, don't replace the value of key1 to new value present in newParams and the keep the same value as oldParams
     *
     * @param newParams An object containing key/value pairs representing the query string
     * @param replace Whether or not to override old params or append
     */
    setQuery(newParams, replace = true) {
        let oldParams = this.getQuery();
        const finalParams = replace
            ? {
                ...oldParams,
                ...newParams,
            }
            : {
                ...newParams,
                ...oldParams,
            };
        const url = WebUrl.buildURLWithParams(this.url, finalParams);
        this.url = new URL(url);
        return this;
    }
    /**
     * Removes the given query param(s) from the query, if present.
     *
     * @param queryParams The queryParam(s) to remove from the query
     */
    removeQuery(...queryParams) {
        queryParams.forEach((queryParam) => {
            if (this.url.searchParams.has(queryParam)) {
                this.url.searchParams.delete(queryParam);
            }
        });
        return this;
    }
    /**
     * Clear the entire query string from the WebUrl.
     */
    clearQuery() {
        const keys = [];
        this.url.searchParams.forEach((_value, key) => {
            keys.push(key);
        });
        this.removeQuery(...keys);
        return this;
    }
    /**
     * Determines whether or not the `otherUrl` matches this WebUrl.
     *
     * The otherUrl matches if
     * - it is a WebUrl, URL, or string
     * - it serializes to the same URL string as this WebUrl, ignoring query
     * parameter order
     *
     * NOTE: If the order of query parameters matters in your comparison,
     * use the `equals` method instead.
     *
     * @param otherUrl
     */
    matches(otherUrl) {
        try {
            const otherSortedWebUrl = new WebUrl(otherUrl instanceof WebUrl ? otherUrl.toString() : otherUrl);
            otherSortedWebUrl.searchParams.sort();
            const thisSortedWebUrl = new WebUrl(this.toString());
            thisSortedWebUrl.searchParams.sort();
            return thisSortedWebUrl.toString() === otherSortedWebUrl.toString();
        }
        catch {
            return false;
        }
    }
    /**
     * Determines whether or not another URL is equal to this WebUrl instance
     *
     * The otherUrl is equal if
     * - it is a WebUrl, URL, or string
     * - it serializes to the same URL string as this WebUrl
     *
     * NOTE: two URLs with the same query parameters but in a different order are not
     * equal according to this comparison. If you don't care about the order of query
     * parameters, use the `matches` method instead.
     *
     * @param otherUrl
     */
    equals(otherUrl) {
        if (otherUrl instanceof WebUrl || otherUrl instanceof URL || typeof otherUrl === 'string') {
            return this.toString() === otherUrl.toString();
        }
        return false;
    }
    /**
     * Returns the URL string represention of the WebUrl instance
     */
    toString() {
        return this.href;
    }
}
